import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InterestPicker } from '@/features/practice/components/InterestPicker'
import { useUpdateInterests } from '@/features/practice/hooks/useInterests'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'

interface OnboardingInterestsStepProps {
  onNext: () => void
  onBack: () => void
}

const MIN_INTERESTS = 1
const MAX_INTERESTS = 5

/**
 * Étape « matières d'intérêt » de l'onboarding (SPEC.md, Lot J) : entre 1 et
 * 5 matières choisies via `InterestPicker` (`features/practice/`), puis
 * `PUT /me/interests`. Reste **passable** — un apprenant qui n'a rien à y
 * gagner tout de suite ne doit jamais être bloqué ici : « Passer » avance
 * sans rien enregistrer, quel que soit l'état de la sélection.
 */
export function OnboardingInterestsStep({ onNext, onBack }: OnboardingInterestsStepProps) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>([])
  const [apiError, setApiError] = useState<string | null>(null)
  const { mutateAsync: updateInterests, isPending } = useUpdateInterests()

  const canContinue = selected.length >= MIN_INTERESTS && selected.length <= MAX_INTERESTS

  const handleContinue = async () => {
    setApiError(null)
    try {
      await updateInterests(selected)
      onNext()
    } catch {
      setApiError(t('onboarding.interests.error'))
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="mb-1">
        <h2 className="text-xl font-semibold text-neutral-900">{t('onboarding.interests.title')}</h2>
        <p className="mt-1 text-sm text-neutral-600">{t('onboarding.interests.subtitle')}</p>
      </div>

      {apiError && <Alert variant="error">{apiError}</Alert>}

      <InterestPicker selected={selected} onChange={setSelected} min={MIN_INTERESTS} max={MAX_INTERESTS} />

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button type="button" variant="secondary" size="lg" className="flex-1" onClick={onBack}>
          {t('onboarding.interests.back')}
        </Button>
        <Button type="button" variant="ghost" size="lg" className="flex-1" onClick={onNext}>
          {t('onboarding.interests.skip')}
        </Button>
        <Button
          type="button"
          size="lg"
          className="flex-1"
          disabled={!canContinue}
          isLoading={isPending}
          onClick={handleContinue}
        >
          {t('onboarding.interests.submit')}
        </Button>
      </div>
    </div>
  )
}
